import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTheme } from '../../Utils/useTheme'

type DropdownItem = {
  value: any
  label: string
  group?: string
}

type Props = {
  items: DropdownItem[]
  value?: any
  onSelect: (item: DropdownItem) => void
  searchable?: boolean
  placeholder?: string
  showGroupTitles?: boolean
  maxHeight?: number
}

export const Dropdown = ({
  items,
  value,
  onSelect,
  searchable = false,
  placeholder = 'Select...',
  showGroupTitles = false,
  maxHeight = 280
}: Props) => {
  const { glass, border, hover, text } = useTheme()
  const ref = useRef<HTMLDivElement>(null)

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const selected = useMemo(() => {
    if (value === undefined || value === null) return null
    return items.find(i => String(i.value) === String(value)) || null
  }, [items, value])

  const filtered = useMemo(() => {
    if (!searchable || !query) return items
    const q = query.toLowerCase()
    return items.filter(i => i.label.toLowerCase().includes(q))
  }, [items, query, searchable])

  const grouped = useMemo(() => {
    if (!showGroupTitles) return { _: filtered }
    const map: Record<string, DropdownItem[]> = {}
    filtered.forEach(i => {
      const g = i.group || '_'
      if (!map[g]) map[g] = []
      map[g].push(i)
    })
    return map
  }, [filtered, showGroupTitles])

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) {
        setOpen(false)
        setQuery('')
      }
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [])

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: 200,zIndex:99999 }}>
      <div
        onClick={() => setOpen(v => !v)}
        style={{
          padding: '10px 12px',
          borderRadius: 14,
          background: glass,
          border: `1px solid ${border}`,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          color: text
        }}
      >
        {searchable && open ? (
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={placeholder}
            autoFocus
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: text,
              fontSize: 14
            }}
            onClick={e => e.stopPropagation()}
          />
        ) : (
          <span
            style={{
              opacity: selected ? 1 : 0.6,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: 14
            }}
          >
            {selected ? selected.label : placeholder}
          </span>
        )}

        <span
          style={{
            fontSize: 16,
            lineHeight: 1,
            opacity: 0.75,
            marginLeft: 6
          }}
        >
          {open ? '▴' : '▾'}
        </span>
      </div>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            zIndex: 50,
            borderRadius: 14,
            background: glass,
            border: `1px solid ${border}`,
            padding: 8,
            backdropFilter: 'blur(14px)'
          }}
        >
          <div
            style={{
              maxHeight,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 2
            }}
          >
            {Object.entries(grouped).map(([group, list]) => (
              <div key={group}>
                {showGroupTitles && group !== '_' && (
                  <div
                    style={{
                      padding: '6px 8px',
                      fontSize: 11,
                      fontWeight: 700,
                      opacity: 0.65,
                      textTransform: 'uppercase'
                    }}
                  >
                    {group}
                  </div>
                )}

                {list.map(i => {
                  const isSelected =
                    selected && String(selected.value) === String(i.value)

                  return (
                    <div
                      key={i.value}
                      onClick={() => {
                        onSelect(i)
                        setOpen(false)
                        setQuery('')
                      }}
                      style={{
                        padding: '9px 10px',
                        borderRadius: 10,
                        cursor: 'pointer',
                        color: text,
                        fontSize: 13,
                        background: isSelected ? hover : 'transparent',
                        fontWeight: isSelected ? 700 : 400
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = hover)}
                      onMouseLeave={e =>
                        (e.currentTarget.style.background =
                          isSelected ? hover : 'transparent')
                      }
                    >
                      {i.label}
                    </div>
                  )
                })}
              </div>
            ))}

            {filtered.length === 0 && (
              <div
                style={{
                  padding: '10px',
                  fontSize: 12,
                  opacity: 0.6,
                  textAlign: 'center'
                }}
              >
                No results
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
